import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { useChatPermissions } from '../../hooks/useChatPermissions';
import ChatSidebar from './ChatSidebar';
import ChatPanel from './ChatPanel';
import NewChatModal from './NewChatModal';

/**
 * ChatSystem - Main chat orchestrator component
 * Apple-inspired messaging UI with light/dark mode support
 */

export default function ChatSystem({ isOpen, onClose, initialChatId = null }) {
  const { user } = useAuth();
  const {
    chats,
    selectedChatId,
    selectedChat,
    selectChat,
    loading,
    showNewChatModal,
    openNewChatModal,
    closeNewChatModal
  } = useChat();
  const { userId } = useChatPermissions();

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  // Handle initial chat selection
  useEffect(() => {
    if (initialChatId && isOpen) {
      selectChat(initialChatId);
    }
  }, [initialChatId, isOpen, selectChat]);

  // Close mobile sidebar when chat is selected
  const handleSelectChat = (chat) => {
    selectChat(chat.id);
    setIsMobileSidebarOpen(false);
  };

  // Handle close
  const handleClose = () => {
    setIsMobileSidebarOpen(false);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#f5f5f7] dark:bg-[#000000] z-50 flex">
      {/* Sidebar */}
      <ChatSidebar
        chats={chats}
        selectedChatId={selectedChatId}
        onSelectChat={handleSelectChat}
        onNewChat={openNewChatModal}
        isMobileOpen={isMobileSidebarOpen}
        onClose={() => setIsMobileSidebarOpen(false)}
        onHome={handleClose}
        currentUserId={userId}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header (when no chat selected) */}
        {!selectedChat && (
          <div className="lg:hidden bg-white/80 dark:bg-[#1c1c1e]/80 backdrop-blur-xl border-b border-[#d2d2d7] dark:border-white/10 px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="w-10 h-10 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-[#1d1d1f] dark:text-white font-semibold">Messages</h1>
            <button
              onClick={handleClose}
              className="w-10 h-10 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Chat Panel */}
        <ChatPanel
          chat={selectedChat}
          onClose={() => {
            if (window.innerWidth < 1024) {
              setIsMobileSidebarOpen(true);
            } else {
              handleClose();
            }
          }}
          onInfoClick={() => setShowInfoPanel(!showInfoPanel)}
        />
      </div>

      {/* Info Panel (future) */}
      {showInfoPanel && selectedChat && (
        <div className="hidden xl:block w-80 bg-white dark:bg-[#1c1c1e] border-l border-[#d2d2d7] dark:border-white/10">
          <div className="p-4 border-b border-[#d2d2d7] dark:border-white/10 flex items-center justify-between">
            <h3 className="text-[#1d1d1f] dark:text-white font-semibold">Details</h3>
            <button
              onClick={() => setShowInfoPanel(false)}
              className="w-8 h-8 rounded-full bg-[#f5f5f7] dark:bg-white/10 hover:bg-[#e8e8ed] dark:hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-4">
            <p className="text-[#86868b] text-sm">Chat details coming soon</p>
          </div>
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && (
        <NewChatModal onClose={closeNewChatModal} />
      )}
    </div>
  );
}
